"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const strategies = [{
  id: "01",
  name: "Market Genesis",
  description: "The foundational stage of identifying high-probability structural shifts using AI-driven pattern recognition across global equity indices and major liquidity zones."
}, {
  id: "02",
  name: "Structural Edge",
  description: "Defining the systematic rules that exploit recurring volatility anomalies in liquid currency pairs to establish a persistent mathematical advantage over market noise."
}, {
  id: "03",
  name: "Tactical Liquidity",
  description: "Deploying capital at the precise apex of confluence where technical signals meet institutional liquidity pools for high-efficiency entries and minimal slippage."
}, {
  id: "04",
  name: "Risk Architecture",
  description: "Protecting equity through rigorous volatility-adjusted position sizing and multi-layered protocols designed to preserve capital during periods of extreme market stress."
}, {
  id: "05",
  name: "Execution Logic",
  description: "The transition from analysis to action, utilizing systematic order routing to eliminate emotional interference and ensure mechanical precision during high-velocity moves."
}, {
  id: "06",
  name: "Performance Audit",
  description: "A continuous feedback loop of statistical review, auditing every trade against the model's parameters to ensure long-term system integrity and growth."
}, {
  id: "07",
  name: "Psychological Mastery",
  description: "The final pillar of systematic excellence, cultivating the discipline required to execute the framework with zero bias and complete confidence in the edge."
}];
const strategyTags = [{
  strategyId: "01",
  tag: "Analysis"
}, {
  strategyId: "01",
  tag: "Equities"
}, {
  strategyId: "01",
  tag: "Patterns"
}, {
  strategyId: "02",
  tag: "Strategy"
}, {
  strategyId: "02",
  tag: "Forex"
}, {
  strategyId: "02",
  tag: "Edge"
}, {
  strategyId: "03",
  tag: "Entry"
}, {
  strategyId: "03",
  tag: "Liquidity"
}, {
  strategyId: "03",
  tag: "Precision"
}, {
  strategyId: "04",
  tag: "Risk"
}, {
  strategyId: "04",
  tag: "Protection"
}, {
  strategyId: "04",
  tag: "Protocols"
}, {
  strategyId: "05",
  tag: "Execution"
}, {
  strategyId: "05",
  tag: "Automation"
}, {
  strategyId: "05",
  tag: "Logic"
}, {
  strategyId: "06",
  tag: "Review"
}, {
  strategyId: "06",
  tag: "Audit"
}, {
  strategyId: "06",
  tag: "Statistics"
}, {
  strategyId: "07",
  tag: "Mindset"
}, {
  strategyId: "07",
  tag: "Discipline"
}, {
  strategyId: "07",
  tag: "Mastery"
}];
const AnimatedFrameworkCard = ({
  strategy,
  index
}: {
  strategy: (typeof strategies)[0];
  index: number;
}) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const tags = strategyTags.filter(t => t.strategyId === strategy.id);
  return <div data-section-id="116875" ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="group flex flex-col items-start gap-6 rounded-none border-border/10 bg-[#141A22] p-8 transition-all duration-500 hover:border-t-2 hover:border-t-[#B8922A] md:flex-row md:gap-12 md:p-[36px]">
        {}
        <div className="flex w-full flex-col gap-2 md:w-1/4 md:gap-4">
          <span className="inline-block w-fit font-sans text-lg tracking-widest text-[#B8922A] transition-transform duration-300 group-hover:rotate-45 md:text-xl">
            ◆ {strategy.id}
          </span>
          <h3 className="font-sans text-lg uppercase tracking-wider text-[#F4EFE6] md:text-xl">
            {strategy.name}
          </h3>
        </div>

        {}
        <div className="flex w-full flex-col gap-6 md:w-3/4">
          <p className="font-serif text-sm leading-relaxed text-[#F4EFE6]/60 md:text-base">
            {strategy.description}
          </p>
          <div className="flex flex-wrap gap-3">
            {tags.map((t, tagIndex) => <Badge key={tagIndex} data-index={tagIndex} variant="outline" className="rounded-none border-[#B8922A]/30 bg-[#B8922A]/5 px-3 py-1.5 font-sans text-[10px] uppercase tracking-widest text-[#B8922A]">
                {t.tag}
              </Badge>)}
          </div>
        </div>
      </Card>
    </div>;
};
export default function TradingStrategiesPreview() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="platform-pillars" className="dark bg-[#0A0E14] text-[#F4EFE6] py-24 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div ref={headerRef} className={`mb-16 text-center transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-sans text-4xl uppercase tracking-[0.2em] text-[#F4EFE6] md:text-5xl">
            Seven Stages. One System.
          </h2>
          <div className="mx-auto mt-6 h-px w-24 bg-[#B8922A]/50"></div>
        </div>

        <div className="flex flex-col gap-6">
          {strategies.map((strategy, i) => <AnimatedFrameworkCard key={strategy.id} strategy={strategy} index={i} />)}
        </div>

        <div className="mt-16 flex justify-center">
          <Button asChild variant="outline" size="lg" className="rounded-none border-[#B8922A] bg-transparent px-8 py-6 font-sans text-sm uppercase tracking-widest text-[#B8922A] transition-all duration-300 hover:bg-[#B8922A] hover:text-[#0A0E14]">
            <Link to="/HowItWorksPage">
              Explore The System <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}